import minBy from 'lodash/minBy';
import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import React from 'react';
import Draggable from 'react-draggable';

const PADDING = 35; // px, has to match with CSS!
const HANDLE_CLASS_NAME = 'bsknacht17-timeline__handle';

function TimelineHandle({stateKey, stepWidth}) {
	return (
		<div
			className={HANDLE_CLASS_NAME}
			style={{
				width: stepWidth,
				marginLeft: `-${Math.round(stepWidth / 2)}px`,
			}}
		>
			<div
				className={`bsknacht17-timeline__handle__dot ${
					stateKey === 'start'
						? 'bsknacht17-timeline__handle__dot--first'
						: 'bsknacht17-timeline__handle__dot--last'
				}`}
			/>
		</div>
	);
}

class Timeline extends React.Component {
	static handleTouchMove(e) {
		e.preventDefault();
	}

	constructor(props) {
		super(props);
		this.state = this.getDimensions();
	}

	componentDidMount() {
		this.updateDimensions = throttle(
			() => {
				this.setState(this.getDimensions());
			},
			100,
			{leading: false, trailing: true}
		);

		window.addEventListener('resize', this.updateDimensions);
		// eslint-disable-next-line react/no-did-mount-set-state
		this.setState(this.getDimensions());
	}

	UNSAFE_componentWillReceiveProps() {
		this.setState(this.getDimensions());
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.updateDimensions);
	}

	getDimensions() {
		const width = this.containerElement
			? this.containerElement.getBoundingClientRect().width
			: window.innerWidth;
		const rangeWidth = width - 2 * PADDING;

		return {
			rangeWidth: rangeWidth,
			stepWidth: rangeWidth / (this.props.steps.length - 1),
			width: width,
		};
	}

	handleHandleStop(key, e, data) {
		let x = Math.round(data.x / this.state.stepWidth);
		x =
			x < 0
				? 0
				: x >= this.props.steps.length
				? this.props.steps.length - 1
				: x; // constraint bounds

		if (key === 'start') {
			x = x < this.props.end ? x : this.props.end - 1; // constraint by other handle
			if (x !== this.props.start) {
				this.props.setStart(x);
			}
		} else {
			x = x > this.props.start ? x : this.props.start + 1; // constraint by other handle
			if (x !== this.props.end) {
				this.props.setEnd(x);
			}
		}
	}

	handleTimeLineClick(e) {
		if (e.target.classList.contains(HANDLE_CLASS_NAME)) {
			return;
		}

		const offsetLeft =
			e.clientX - e.currentTarget.getBoundingClientRect().left;
		const x = offsetLeft / this.state.stepWidth;
		const normalizedX = Math.round(x);
		const key = minBy(
			['start', 'end'].map((rangeKey) => ({
				key: rangeKey,
				x: Math.abs(x - this.props[rangeKey]),
			})),
			'x'
		).key;

		if (key === 'start') {
			if (normalizedX !== this.props.start) {
				this.props.setStart(normalizedX);
			}
		} else {
			if (normalizedX !== this.props.end) {
				this.props.setEnd(normalizedX);
			}
		}
	}

	render() {
		/**
		 * @param {"start" | "end"} stateKey key
		 * @returns {Partial<import('react-draggable').DraggableProps>} Draggable props
		 */
		const draggableProps = (stateKey) => ({
			axis: 'x',
			handle: `.${HANDLE_CLASS_NAME}`,
			bounds: {left: 0, right: this.state.rangeWidth, top: 0, bottom: 0},
			position: {
				x: Math.round(this.props[stateKey] * this.state.stepWidth),
				y: 0,
			},
			onStop: (e, data) => {
				e.preventDefault();
				e.stopPropagation();
				data.node.blur();
				this.handleHandleStop(stateKey, e, data);
			},
		});

		const lineBaseStyle = {
			marginLeft: `-${Math.round(this.state.stepWidth / 2)}px`,
			width: this.state.stepWidth,
		};

		const markClasses = (n) =>
			`bsknacht17-timeline__mark ${
				n === this.props.start
					? 'bsknacht17-timeline__mark--selected bsknacht17-timeline__mark--selected-first'
					: n === this.props.end
					? 'bsknacht17-timeline__mark--selected bsknacht17-timeline__mark--selected-last'
					: n > this.props.start && n < this.props.end
					? 'bsknacht17-timeline__mark--selected'
					: ''
			}`;

		return (
			<div
				className="bsknacht17-timeline"
				ref={(ref) => {
					this.containerElement = ref;
				}}
				onTouchMove={Timeline.handleTouchMove}
			>
				<div
					className="bsknacht17-timeline__handle-container"
					onClick={this.handleTimeLineClick.bind(this)}
				>
					{this.props.steps.map((label, n) => (
						<div
							className={markClasses(n)}
							key={n}
							style={{
								...lineBaseStyle,
								left: Math.round(n * this.state.stepWidth),
							}}
						>
							<span className="bsknacht17-timeline__mark__line" />
							{n !== this.props.steps.length - 1 && (
								<span
									className="bsknacht17-timeline__mark__line bsknacht17-timeline__mark__line--sub"
									style={{
										marginLeft: Math.round(
											this.state.stepWidth / 2
										),
									}}
								/>
							)}
							<div className="bsknacht17-timeline__mark__label">
								{label}
							</div>
						</div>
					))}

					<div
						className="bsknacht17-timeline__bar"
						style={{
							left:
								Math.round(
									this.props.start * this.state.stepWidth
								) || 0,
							right:
								this.state.rangeWidth -
									Math.round(
										this.props.end * this.state.stepWidth
									) || 0,
						}}
					/>

					<Draggable {...draggableProps('start')}>
						<div style={{position: 'relative', zIndex: 100}}>
							<TimelineHandle
								stepWidth={this.state.stepWidth}
								stateKey="start"
							/>
						</div>
					</Draggable>
					<Draggable {...draggableProps('end')}>
						<div style={{position: 'relative', zIndex: 100}}>
							<TimelineHandle
								stepWidth={this.state.stepWidth}
								stateKey="end"
							/>
						</div>
					</Draggable>
				</div>
			</div>
		);
	}
}

Timeline.propTypes = {
	setStart: PropTypes.func.isRequired,
	setEnd: PropTypes.func.isRequired,
	start: PropTypes.number.isRequired,
	end: PropTypes.number.isRequired,
	steps: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default Timeline;
